const FORM_SELECTOR = '.form__block';
const FIELD_WRAP_SELECTOR = '.form__input-wrap';
const FIELD_SELECTOR = '.form__input';
const LABEL_SELECTOR = '.form__input-label';
const SUBMIT_LINK_SELECTOR = '.content__btn';
const ERROR_CLASS = 'is-error';

const getFieldLabel = (field) =>
  field.dataset.name || field.name || field.id || 'This field';

const getDefaultLabelText = (label) => {
  if (!label.dataset.defaultText) {
    label.dataset.defaultText = label.textContent.trim();
  }

  return label.dataset.defaultText;
};

const getErrorMessage = (field, label) => {
  const defaultLabel = getDefaultLabelText(label).toLowerCase();
  const value = field.value.trim();

  if (field.required && !value) {
    return `Please enter ${defaultLabel}`;
  }

  if (field.type === 'email' && value && !field.validity.valid) {
    return 'Please enter a valid email address';
  }

  if (!field.validity.valid) {
    return field.validationMessage || `Please check ${getFieldLabel(field)}`;
  }

  return '';
};

const setFieldState = (wrap, field, label, errorMessage) => {
  const hasError = Boolean(errorMessage);

  wrap.classList.toggle(ERROR_CLASS, hasError);
  field.setAttribute('aria-invalid', String(hasError));
  label.textContent = hasError ? errorMessage : getDefaultLabelText(label);

  if (hasError) {
    field.dataset.wasInvalid = 'true';
    return;
  }

  delete field.dataset.wasInvalid;
};

const validateField = (wrap) => {
  const field = wrap.querySelector(FIELD_SELECTOR);
  const label = wrap.querySelector(LABEL_SELECTOR);

  if (!field || !label) {
    return true;
  }

  const errorMessage = getErrorMessage(field, label);

  setFieldState(wrap, field, label, errorMessage);

  return !errorMessage;
};

const validateForm = (form) => {
  const wraps = Array.from(form.querySelectorAll(FIELD_WRAP_SELECTOR));
  let invalidWrap = null;

  wraps.forEach((wrap) => {
    if (!validateField(wrap) && !invalidWrap) {
      invalidWrap = wrap;
    }
  });

  if (invalidWrap) {
    invalidWrap.querySelector(FIELD_SELECTOR)?.focus();
    return false;
  }

  return true;
};

const bindField = (wrap) => {
  const field = wrap.querySelector(FIELD_SELECTOR);
  const label = wrap.querySelector(LABEL_SELECTOR);

  if (!field || !label) {
    return;
  }

  getDefaultLabelText(label);

  field.addEventListener('blur', () => {
    if (field.value.trim() || field.dataset.wasInvalid === 'true') {
      validateField(wrap);
    }
  });

  field.addEventListener('input', () => {
    if (field.dataset.wasInvalid === 'true') {
      validateField(wrap);
    }
  });
};

const bindSubmitLinks = (form) => {
  form.querySelectorAll(SUBMIT_LINK_SELECTOR).forEach((link) => {
    link.addEventListener('click', (event) => {
      event.preventDefault();

      if (typeof form.requestSubmit === 'function') {
        form.requestSubmit();
        return;
      }

      form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
    });
  });
};

const bindForm = (form) => {
  if (form.dataset.validationReady === 'true') {
    return;
  }

  form.dataset.validationReady = 'true';
  form.noValidate = true;

  form.querySelectorAll(FIELD_WRAP_SELECTOR).forEach(bindField);
  bindSubmitLinks(form);

  form.addEventListener(
    'submit',
    (event) => {
      if (!validateForm(form)) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    },
    true
  );
};

export const initFormValidation = () => {
  document.querySelectorAll(FORM_SELECTOR).forEach(bindForm);
};
